// SCR-STAFF-904: وضعیت اشتراک، خرید و تمدید، تغییر پلن، صورتحساب‌ها و محیط نمایشی (AP-PLT-07، US-PLT-021 تا 024، 027، 053)؛ اعتبار پیامک با
// موجودی، سقف منفی تراکنشی و دفتر حرکات (US-PLT-026؛ شارژ از Console — C-AGENT-414) و مصرف دوره جاری (US-PLT-025: ارائه‌دهنده فعال با بیشینه
// همزمان، قطعه پیامک، فضای ذخیره‌سازی با هشدار ۸۰٪).
import Alert from '@mui/material/Alert';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type SmsCreditLedgerView, type UsageMeterView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  DataTable,
  ErrorState,
  faDigits,
  JalaliDate,
  KeyValueList,
  ListPage,
  LoadingState,
  type DataTableColumn,
} from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { pltKeys, toCount } from './common';
import {
  DemoEnvironmentSection,
  InvoicesSection,
  PlanChangeSection,
  PurchaseSection,
  SubscriptionPlanSection,
  useSubscriptionOverview,
} from './SubscriptionBilling';

export const SCR_STAFF_904 = 'SCR-STAFF-904';

const GB = 1024 * 1024 * 1024;

function formatMetric(metric: string, value: number): string {
  if (metric === 'storage_bytes') {
    return faDigits((value / GB).toFixed(value >= 10 * GB ? 0 : 2));
  }
  return faDigits(value.toLocaleString('en-US'));
}

function UsageRow({ meter }: { meter: UsageMeterView }) {
  const { t } = useTranslation('plt');
  const current = toCount(
    meter.metric === 'sms_parts'
      ? meter.total
      : meter.metric === 'active_providers'
        ? meter.peak
        : meter.current,
  );
  const limit = meter.limit === null || meter.limit === undefined ? null : toCount(meter.limit);
  const percent = limit ? Math.min(100, Math.round((current / limit) * 100)) : null;
  return (
    <Stack spacing={0.5} data-testid={`usage-${meter.metric}`}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="body2">
          {t(`plt:subscription.metrics.${meter.metric}`, { defaultValue: meter.metric })}
        </Typography>
        <Typography variant="body2" data-testid={`usage-${meter.metric}-value`}>
          {limit === null
            ? t(`plt:subscription.unit.${meter.metric}`, { value: formatMetric(meter.metric, current) })
            : t('plt:subscription.of_limit', {
                value: t(`plt:subscription.unit.${meter.metric}`, {
                  value: formatMetric(meter.metric, current),
                }),
                limit: t(`plt:subscription.unit.${meter.metric}`, {
                  value: formatMetric(meter.metric, limit),
                }),
              })}
        </Typography>
      </Stack>
      {percent !== null && (
        <LinearProgress
          variant="determinate"
          value={percent}
          color={percent >= 80 ? 'warning' : 'primary'}
          aria-label={t(`plt:subscription.metrics.${meter.metric}`, { defaultValue: meter.metric })}
        />
      )}
      {percent !== null && percent >= 80 && (
        <Typography variant="caption" color="warning.main" data-testid={`usage-${meter.metric}-warning`}>
          {t('plt:subscription.near_limit', { percent: faDigits(percent) })}
        </Typography>
      )}
    </Stack>
  );
}

export function SubscriptionPage() {
  const { t } = useTranslation('plt');
  const { tenantApi, session } = useTenantSession();

  const credit = useQuery({
    queryKey: pltKeys.smsCredit(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/sms-credit', { params: { query: { limit: 20 } } })),
  });
  const usage = useQuery({
    queryKey: pltKeys.usage(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/platform/usage')),
  });
  const overview = useSubscriptionOverview();

  const columns: DataTableColumn<SmsCreditLedgerView>[] = [
    {
      id: 'occurredAt',
      headerKey: 'plt:subscription.ledger.at',
      cell: (row) => <JalaliDate value={row.occurredAt} withTime />,
    },
    {
      id: 'type',
      headerKey: 'plt:subscription.ledger.type',
      cell: (row) => t(`plt:subscription.entry.${row.entryType}`),
    },
    {
      id: 'delta',
      headerKey: 'plt:subscription.ledger.delta',
      cell: (row) => (
        <bdi dir="ltr">
          {faDigits(toCount(row.delta) > 0 ? `+${toCount(row.delta)}` : String(toCount(row.delta)))}
        </bdi>
      ),
    },
    {
      id: 'balance',
      headerKey: 'plt:subscription.ledger.balance',
      cell: (row) => <bdi dir="ltr">{faDigits(toCount(row.balanceAfter))}</bdi>,
    },
    {
      id: 'purpose',
      headerKey: 'plt:subscription.ledger.purpose',
      cell: (row) =>
        row.purpose ? t(`plt:subscription.purpose.${row.purpose}`) : (row.reason ?? t('common:states.none')),
    },
  ];

  const balance = credit.data?.balance;
  const value = toCount(balance?.balance);
  return (
    <ListPage
      screenId={SCR_STAFF_904}
      titleKey="plt:subscription.title"
      subtitleKey="plt:subscription.subtitle"
    >
      <Stack spacing={2}>
        {overview.isPending ? <LoadingState rows={3} /> : null}
        {overview.error ? <ErrorState error={overview.error} onRetry={() => void overview.refetch()} /> : null}
        {overview.data ? (
          <>
            <SubscriptionPlanSection overview={overview.data} />
            <PurchaseSection key={`${overview.data.status}-${overview.data.planCode}`} overview={overview.data} />
            <PlanChangeSection overview={overview.data} />
          </>
        ) : null}
        <InvoicesSection />
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="sms-credit">
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('plt:subscription.sms_title')}
          </Typography>
          {credit.isPending ? (
            <LoadingState rows={2} />
          ) : credit.error ? (
            <ErrorState error={credit.error} onRetry={() => void credit.refetch()} />
          ) : balance ? (
            <Stack spacing={2}>
              <KeyValueList
                columns={3}
                items={[
                  {
                    labelKey: 'plt:subscription.balance',
                    value: <bdi dir="ltr">{faDigits(value)}</bdi>,
                    testId: 'sms-balance',
                  },
                  {
                    labelKey: 'plt:subscription.negative_cap',
                    value: faDigits(toCount(balance.negativeCap)),
                  },
                  {
                    labelKey: 'plt:subscription.last_charge',
                    value: faDigits(toCount(balance.lastChargeParts)),
                  },
                ]}
              />
              {!balance.marketingAllowed && (
                <Alert severity="error" data-testid="sms-marketing-blocked">
                  {t('plt:subscription.marketing_blocked', { cap: faDigits(toCount(balance.negativeCap)) })}
                </Alert>
              )}
              {balance.marketingAllowed && balance.lowBalance && (
                <Alert severity="warning" data-testid="sms-low-balance">
                  {t('plt:subscription.low_balance')}
                </Alert>
              )}
              <Typography variant="body2" color="text.secondary">
                {t('plt:subscription.charge_help')}
              </Typography>
              <DataTable
                captionKey="plt:subscription.ledger.caption"
                columns={columns}
                rows={credit.data?.entries}
                getRowId={(row) => row.id}
                emptyTitleKey="plt:subscription.ledger.empty"
              />
            </Stack>
          ) : null}
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }} data-testid="usage">
          <Typography variant="h6" sx={{ mb: 1 }}>
            {t('plt:subscription.usage_title')}
          </Typography>
          {usage.isPending ? (
            <LoadingState rows={2} />
          ) : usage.error ? (
            <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
          ) : (
            <Stack spacing={2}>
              {(usage.data?.items ?? []).map((meter) => (
                <UsageRow key={meter.metric} meter={meter} />
              ))}
            </Stack>
          )}
        </Paper>
        <DemoEnvironmentSection />
      </Stack>
    </ListPage>
  );
}
